import React, { useEffect, useState } from 'react';
import api from '../api';

const card = { background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', boxShadow: '0 1px 4px rgba(15,23,42,0.05)' };
const muted = { color: '#64748b', fontSize: '12px' };

function euro(v) {
  return Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

export default function ReportsPage() {
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [report, setReport] = useState(null);
  const [yearReport, setYearReport] = useState(null);
  const [htmlPreview, setHtmlPreview] = useState('');
  const [snapshots, setSnapshots] = useState([]);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [compareA, setCompareA] = useState('');
  const [compareB, setCompareB] = useState('');
  const [compare, setCompare] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { load(); }, []);
  useEffect(() => { if (month) loadReport(month); }, [month]);
  useEffect(() => { if (year) loadYear(year); }, [year]);

  async function load() {
    setError('');
    try {
      const [m, y, s] = await Promise.all([api.get('/reports/months'), api.get('/reports/years'), api.get('/reports/snapshots')]);
      setMonths(m.data);
      setYears(y.data);
      setSnapshots(s.data);
      if (!month && m.data[0]) setMonth(m.data[0].monat);
      if (!year && y.data[0]) setYear(y.data[0].jahr);
      if (!compareA && s.data[0]) setCompareA(String(s.data[0].id));
      if (!compareB && s.data[1]) setCompareB(String(s.data[1].id));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Berichte konnten nicht geladen werden');
    }
  }

  async function loadReport(monat) {
    const [json, html] = await Promise.all([
      api.get('/reports/monthly', { params: { monat } }),
      api.get('/reports/monthly.html', { params: { monat }, responseType: 'text' })
    ]);
    setReport(json.data);
    setHtmlPreview(html.data);
  }

  async function loadYear(jahr) {
    const r = await api.get('/reports/yearly', { params: { jahr } });
    setYearReport(r.data);
  }

  async function download(path, filename) {
    try {
      const r = await api.get(path, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Export fehlgeschlagen');
    }
  }

  async function saveSnapshot() {
    if (!report) return;
    const r = await api.post('/reports/snapshots', { typ: 'monthly', titel: `Monatsbericht ${report.monat}`, zeitraum: report.monat, daten: report });
    setMessage(`Snapshot ${r.data.id} gespeichert.`);
    await load();
  }

  async function openSnapshot(id) {
    const r = await api.get(`/reports/snapshots/${id}`);
    setSelectedSnapshot(r.data);
  }

  async function runCompare() {
    if (!compareA || !compareB || compareA === compareB) return;
    const r = await api.get('/reports/snapshot-compare', { params: { a: compareA, b: compareB } });
    setCompare(r.data);
  }

  function printPreview() {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(htmlPreview);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
        <div>
          <h1 style={{ margin: 0, color: '#0f172a', fontSize: '23px' }}>Berichte und Exporte</h1>
          <p style={{ ...muted, margin: '4px 0 0' }}>Vorschau, Druck, Jahresbericht und Snapshot-Vergleich.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select value={month} onChange={e => setMonth(e.target.value)} style={input}>
            {months.map(m => <option key={m.monat} value={m.monat}>{m.monat} ({m.anzahl})</option>)}
          </select>
          <select value={year} onChange={e => setYear(e.target.value)} style={input}>
            {years.map(y => <option key={y.jahr} value={y.jahr}>{y.jahr} ({y.anzahl})</option>)}
          </select>
        </div>
      </div>

      {error && <Notice tone="red">{error}</Notice>}
      {message && <Notice tone="green">{message}</Notice>}

      {report && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', marginBottom: '12px' }}>
            <Stat title="Ausgaben" value={euro(report.summary.ausgaben)} />
            <Stat title="Einnahmen" value={euro(report.summary.einnahmen)} />
            <Stat title="Saldo" value={euro(report.summary.saldo)} />
            <Stat title="Buchungen" value={report.summary.anzahl || 0} />
          </div>

          <section style={{ ...card, marginBottom: '12px' }}>
            <h2 style={h2}>Monatsbericht {report.monat}</h2>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
              <button style={button('#2563eb')} onClick={() => download(`/reports/monthly.html?monat=${encodeURIComponent(report.monat)}`, `finanzanalyse-monatsbericht-${report.monat}.html`)}>HTML Download</button>
              <button style={button('#0f172a')} onClick={printPreview}>Drucken</button>
              <button style={button('#16a34a')} onClick={saveSnapshot}>Snapshot speichern</button>
              <button style={button('#7c3aed')} onClick={() => download('/reports/cancellations.csv', 'finanzanalyse-kuendigungsliste.csv')}>Kündigungsliste CSV</button>
              <button style={button('#475569')} onClick={() => download('/reports/rules.json', 'finanzanalyse-regelbericht.json')}>Regelbericht JSON</button>
            </div>
            <iframe title="Berichtsvorschau" srcDoc={htmlPreview} style={{ width: '100%', height: '460px', border: '1px solid #cbd5e1', borderRadius: '8px', background: 'white' }} />
          </section>
        </>
      )}

      {yearReport && (
        <section style={{ ...card, marginBottom: '12px' }}>
          <h2 style={h2}>Jahresbericht {yearReport.jahr}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', marginBottom: '12px' }}>
            <Stat title="Ausgaben" value={euro(yearReport.summary.ausgaben)} />
            <Stat title="Einnahmen" value={euro(yearReport.summary.einnahmen)} />
            <Stat title="Saldo" value={euro(yearReport.summary.saldo)} />
            <Stat title="Buchungen" value={yearReport.summary.anzahl || 0} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <h3 style={h3}>Monate</h3>
              {yearReport.monate.map(m => <Row key={m.monat} left={m.monat} sub={`${m.anzahl} Buchungen`} right={euro(m.saldo)} />)}
            </div>
            <div>
              <h3 style={h3}>Kategorien</h3>
              {yearReport.kategorien.slice(0, 10).map(k => <Row key={k.kategorie} left={k.kategorie} sub={`${k.anzahl} Buchungen`} right={euro(k.gesamt)} />)}
            </div>
          </div>
        </section>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <section style={card}>
          <h2 style={h2}>Snapshot-Historie</h2>
          {snapshots.length === 0 && <div style={muted}>Noch keine Snapshots.</div>}
          {snapshots.map(s => (
            <button key={s.id} onClick={() => openSnapshot(s.id)} style={{ width: '100%', textAlign: 'left', border: '1px solid #e2e8f0', borderRadius: '9px', background: 'white', padding: '9px', marginBottom: '8px', cursor: 'pointer' }}>
              <strong style={{ color: '#0f172a' }}>{s.titel}</strong>
              <div style={muted}>{s.typ} - {s.zeitraum || '-'} - {new Date(s.created_at).toLocaleString('de-DE')}</div>
            </button>
          ))}
        </section>

        <section style={card}>
          <h2 style={h2}>Snapshot-Vergleich</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', marginBottom: '10px' }}>
            <select value={compareA} onChange={e => setCompareA(e.target.value)} style={input}>
              <option value="">Snapshot A</option>
              {snapshots.map(s => <option key={s.id} value={s.id}>{s.id}: {s.zeitraum || s.titel}</option>)}
            </select>
            <select value={compareB} onChange={e => setCompareB(e.target.value)} style={input}>
              <option value="">Snapshot B</option>
              {snapshots.map(s => <option key={s.id} value={s.id}>{s.id}: {s.zeitraum || s.titel}</option>)}
            </select>
            <button style={button('#2563eb')} onClick={runCompare}>Vergleichen</button>
          </div>
          {compare && (
            <div>
              <Row left="Ausgaben" sub={`${compare.a.titel} -> ${compare.b.titel}`} right={euro(compare.differenz.ausgaben)} />
              <Row left="Einnahmen" sub="Differenz" right={euro(compare.differenz.einnahmen)} />
              <Row left="Saldo" sub="Differenz" right={euro(compare.differenz.saldo)} />
              <Row left="Buchungen" sub="Differenz" right={compare.differenz.buchungen} />
            </div>
          )}
          {selectedSnapshot && (
            <div style={{ marginTop: '14px' }}>
              <h3 style={h3}>Snapshot Details</h3>
              <pre style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px', overflow: 'auto', maxHeight: '280px', fontSize: '11px' }}>{JSON.stringify(selectedSnapshot.daten, null, 2)}</pre>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const h2 = { margin: '0 0 10px', color: '#0f172a', fontSize: '16px' };
const h3 = { margin: '0 0 8px', color: '#0f172a', fontSize: '14px' };
const input = { padding: '9px', borderRadius: '9px', border: '1px solid #cbd5e1', background: 'white' };

function button(bg) {
  return { background: bg, color: 'white', border: 'none', borderRadius: '9px', padding: '9px 11px', cursor: 'pointer', fontWeight: 900, fontSize: '12px' };
}

function Stat({ title, value }) {
  return <div style={card}><div style={{ ...muted, fontWeight: 800, textTransform: 'uppercase' }}>{title}</div><div style={{ color: '#0f172a', fontWeight: 950, fontSize: '21px', marginTop: '5px' }}>{value}</div></div>;
}

function Row({ left, sub, right }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '8px 0', borderTop: '1px solid #f1f5f9' }}><div><div style={{ color: '#0f172a', fontWeight: 800, fontSize: '13px' }}>{left}</div><div style={muted}>{sub}</div></div><div style={{ color: '#2563eb', fontWeight: 950, whiteSpace: 'nowrap' }}>{right}</div></div>;
}

function Notice({ tone, children }) {
  const map = tone === 'red' ? ['#fee2e2', '#991b1b', '#fecaca'] : ['#dcfce7', '#166534', '#86efac'];
  return <div style={{ background: map[0], color: map[1], border: `1px solid ${map[2]}`, borderRadius: '10px', padding: '10px 12px', marginBottom: '12px', fontWeight: 700 }}>{children}</div>;
}
